<template>
  <div :class="depth > 0 ? 'qb-subgroup border rounded p-2 mb-1' : 'qb-group condition-group mb-2'">
    <!-- En-tête : ET/OU + boutons actions -->
    <div class="d-flex align-items-center mb-2" style="gap:8px">
      <span class="font-weight-bold">Condition</span>
      <b-button-group size="sm">
        <b-button
          :variant="localOp === 'ET' ? 'primary' : 'outline-primary'"
          :style="localOp === 'ET' ? 'font-weight:600' : 'opacity:0.55'"
          @click="setOperator('ET')"
        >ET</b-button>
        <b-button
          :variant="localOp === 'OU' ? 'primary' : 'outline-primary'"
          :style="localOp === 'OU' ? 'font-weight:600' : 'opacity:0.55'"
          @click="setOperator('OU')"
        >OU</b-button>
      </b-button-group>
      <div class="ml-auto d-flex" style="gap:4px">
        <b-button size="sm" variant="outline-primary" @click="addRule">
          <i class="fas fa-plus"></i> Ajouter une règle
        </b-button>
        <b-button size="sm" variant="outline-primary" @click="addSubGroup">
          <i class="fas fa-plus-circle"></i> Ajouter un groupe
        </b-button>
        <b-button v-if="canRemove" size="sm" variant="outline-danger" @click="$emit('remove')">
          <i class="fas fa-times"></i> Supprimer
        </b-button>
      </div>
    </div>

    <!-- Liste des conditions / sous-groupes -->
    <div class="pl-3 border-left" style="border-left-width:2px!important;border-color:#007bff!important">
      <div v-for="(item, idx) in group.conditions" :key="idx" class="mb-1">

        <!-- Sous-groupe récursif -->
        <query-builder-group
          v-if="item.isGroup"
          :group="item"
          :fields="fields"
          :categories-map="categoriesMap"
          :marques-list="marquesList"
          :can-remove="true"
          :depth="depth + 1"
          @remove="removeItem(idx)"
          @open-category="$emit('open-category', $event)"
        />

        <!-- Règle simple -->
        <div v-else class="d-flex align-items-center" style="gap:4px">
          <!-- Champ -->
          <b-form-select v-model="item.field" size="sm" style="width:130px" @change="onFieldChange(item)">
            <option v-for="f in fields" :key="f.value" :value="f.value">{{ f.label }}</option>
          </b-form-select>

          <!-- Opérateur -->
          <b-form-select v-model="item.operator" size="sm" style="width:130px">
            <template v-if="getFieldType(item.field) === 'number'">
              <option value=">">supérieur à</option>
              <option value=">=">supérieur ou égal</option>
              <option value="<">inférieur à</option>
              <option value="<=">inférieur ou égal</option>
              <option value="==">égal à</option>
            </template>
            <template v-else-if="getFieldType(item.field) === 'categorie'">
              <option value="==">est égal à</option>
              <option value="!=">n'est pas égal à</option>
            </template>
            <template v-else-if="getFieldType(item.field) === 'categories'">
              <option value="==">contient la catégorie</option>
              <option value="!=">ne contient pas la catégorie</option>
            </template>
            <template v-else>
              <option value="==">est égal à</option>
              <option value="!=">n'est pas</option>
              <option value="LIKE">contient</option>
              <option value="NOT LIKE">ne contient pas</option>
            </template>
          </b-form-select>

          <!-- Valeur : catégorie (sélecteur modal) -->
          <div v-if="getFieldType(item.field) === 'categorie' || getFieldType(item.field) === 'categories'" class="d-flex align-items-center" style="flex:1;gap:4px">
            <b-button size="sm" variant="outline-primary" @click="$emit('open-category', item)" style="flex:1;text-align:left">
              <span v-if="item.value"><i class="fas fa-folder"></i> {{ categoriesMap[item.value] || item.value }}</span>
              <span v-else class="text-muted"><i class="fas fa-folder-open"></i> Sélectionner</span>
            </b-button>
            <b-button v-if="item.value" size="sm" variant="outline-secondary" @click="item.value = ''">
              <i class="fas fa-times"></i>
            </b-button>
          </div>

          <!-- Valeur : nombre -->
          <b-form-input
            v-else-if="getFieldType(item.field) === 'number'"
            v-model="item.value"
            type="number"
            step="0.01"
            size="sm"
            style="flex:1"
            placeholder="Valeur..."
          />

          <!-- Valeur : texte avec autocomplete marques -->
          <b-form-input
            v-else-if="fieldUsesMarquesList(item.field)"
            v-model="item.value"
            size="sm"
            :list="marqDatalistId"
            placeholder="Valeur..."
            style="flex:1"
          />

          <!-- Valeur : texte simple -->
          <b-form-input
            v-else
            v-model="item.value"
            size="sm"
            placeholder="Valeur..."
            style="flex:1"
          />

          <!-- Supprimer la règle -->
          <b-button size="sm" variant="outline-danger" @click="removeItem(idx)" v-if="group.conditions.length > 1">
            <i class="fas fa-times"></i>
          </b-button>
        </div>
      </div>
    </div>

    <!-- Datalist autocomplete marques (une fois par composant, id unique) -->
    <datalist :id="marqDatalistId">
      <option v-for="m in marquesList" :key="m" :value="m" />
    </datalist>
  </div>
</template>

<script>
export default {
  name: 'QueryBuilderGroup',

  props: {
    /** Le groupe : { operator: 'ET'|'OU', conditions: [...] } */
    group: { type: Object, required: true },
    /** Définition des champs : [{ value, label, type: 'text'|'number'|'categorie', useMarquesList? }] */
    fields: { type: Array, default: () => [] },
    /** Map id → nom pour afficher le nom de catégorie */
    categoriesMap: { type: Object, default: () => ({}) },
    /** Liste de marques pour autocomplete */
    marquesList: { type: Array, default: () => [] },
    /** Le bouton Supprimer est visible */
    canRemove: { type: Boolean, default: false },
    /** Profondeur (0 = groupe racine, >0 = sous-groupe) */
    depth: { type: Number, default: 0 },
  },

  data() {
    return {
      localOp: this.group.operator || 'ET',
      marqDatalistId: 'marques-qb-' + Math.random().toString(36).slice(2, 9),
    };
  },

  watch: {
    'group.operator'(val) {
      this.localOp = val || 'ET';
    },
  },

  methods: {
    setOperator(op) {
      this.localOp = op;
      this.$set(this.group, 'operator', op);
    },

    addRule() {
      const first = this.fields[0];
      const defaultOp = first && first.type === 'number' ? '>' : '==';
      this.group.conditions.push({
        field: first ? first.value : '',
        operator: defaultOp,
        value: '',
      });
    },

    addSubGroup() {
      const first = this.fields[0];
      const defaultOp = first && first.type === 'number' ? '>' : '==';
      // $set pour que Vue 2 détecte l'ajout à l'index
      this.$set(this.group.conditions, this.group.conditions.length, {
        isGroup: true,
        operator: 'ET',
        conditions: [{ field: first ? first.value : '', operator: defaultOp, value: '' }],
      });
    },

    removeItem(idx) {
      this.group.conditions.splice(idx, 1);
    },

    onFieldChange(rule) {
      const type = this.getFieldType(rule.field);
      rule.operator = type === 'number' ? '>' : '==';
      rule.value = '';
    },

    getFieldType(fieldValue) {
      const f = this.fields.find(f => f.value === fieldValue);
      return f ? f.type : 'text';
    },

    fieldUsesMarquesList(fieldValue) {
      const f = this.fields.find(f => f.value === fieldValue);
      return f ? !!f.useMarquesList : false;
    },
  },
};
</script>

<style scoped>
.qb-subgroup {
  background: #f8f9fa;
}
</style>
